/**
 * Position Overlay Manager
 * Manages position-related lines on the TradingView chart
 * Uses createShape() API (Charting Library edition - Trading Platform not available)
 * 
 * Note: createPositionLine()/createOrderLine() require Trading Platform edition
 * We use shapes instead, which are available in standard Charting Library
 * 
 * Draggable TP/SL: Take Profit and Stop Loss lines can be dragged to modify prices
 * Uses debouncing to prevent spam - only triggers callback after user stops dragging
 * 
 * Partial Targets: Now supports multiple TP/SL targets for partial position closes
 */

import type { Position } from "@/solana/programs/types";
import { validateTPPrice, validateSLPrice } from "./tpsl-handler";

export interface TPSLChangeCallback {
  onTPSLChange?: (takeProfit: number | null, stopLoss: number | null) => void;
}

export interface TPSLModifyCallback {
  (type: "tp" | "sl", newPrice: number, position: Position): void;
}

// Partial target interface (simplified from API)
export interface PartialTarget {
  id: string;
  targetType: "take_profit" | "stop_loss";
  triggerPrice: number;
  closeAmount: number;
  closeAmountType: "percentage" | "absolute";
  closeAmountValue: number;
}

// Debounce delay in milliseconds - wait this long after user stops dragging
const DEBOUNCE_DELAY_MS = 1500;

export class PositionOverlay {
  private entryLineId: TradingView.EntityId | null = null;
  private liquidationLineId: TradingView.EntityId | null = null;
  private tpLineId: TradingView.EntityId | null = null;
  private slLineId: TradingView.EntityId | null = null;
  private currentPosition: Position | null = null;
  private chart: TradingView.IChartWidgetApi | null = null;
  
  // Partial targets line IDs
  private partialTpLineIds: TradingView.EntityId[] = [];
  private partialSlLineIds: TradingView.EntityId[] = [];
  
  // For draggable TP/SL
  private onModifyCallback: TPSLModifyCallback | null = null;
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private lastKnownTpPrice: number | null = null;
  private lastKnownSlPrice: number | null = null;
  private isModifyingTp = false;
  private isModifyingSl = false;
  
  // Debounce timers - only fire callback after user stops dragging
  private tpDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private slDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingTpPrice: number | null = null;
  private pendingSlPrice: number | null = null;

  /**
   * Set the callback for when TP/SL lines are modified (dragged)
   */
  setOnModifyCallback(callback: TPSLModifyCallback | null): void {
    this.onModifyCallback = callback;
  }

  /**
   * Start checking for price changes on draggable TP/SL lines
   */
  private startPriceCheck(): void {
    if (this.checkInterval) return;

    // Check every 300ms for price changes (faster polling, but debounced callback)
    this.checkInterval = setInterval(() => {
      this.checkForPriceChanges();
    }, 300);
  }

  /**
   * Stop checking for price changes
   */
  private stopPriceCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    // Clear any pending debounce timers
    if (this.tpDebounceTimer) {
      clearTimeout(this.tpDebounceTimer);
      this.tpDebounceTimer = null;
    }
    if (this.slDebounceTimer) {
      clearTimeout(this.slDebounceTimer);
      this.slDebounceTimer = null;
    }
  }

  /**
   * Check if TP/SL prices have changed (user dragged them)
   * Uses debouncing to only trigger callback after user stops dragging
   */
  private checkForPriceChanges(): void {
    if (!this.chart || !this.onModifyCallback || !this.currentPosition) return;

    // Check TP line
    if (this.tpLineId && !this.isModifyingTp) {
      try {
        const shape = this.chart.getShapeById(this.tpLineId);
        if (shape) {
          const points = shape.getPoints();
          if (points && points.length > 0) {
            const currentPrice = points[0].price;
            const lastKnown = this.lastKnownTpPrice;
            
            if (lastKnown !== null) {
              const priceChangePercent = Math.abs((currentPrice - lastKnown) / lastKnown);
              if (priceChangePercent > 0.00001) {
                // Price changed! Update tracking and schedule debounced callback
                this.lastKnownTpPrice = currentPrice;
                
                // Validate the new price
                if (validateTPPrice(currentPrice, this.currentPosition)) {
                  // Schedule debounced callback - reset timer if already pending
                  if (this.tpDebounceTimer) {
                    clearTimeout(this.tpDebounceTimer);
                  }
                  this.pendingTpPrice = currentPrice;
                  
                  this.tpDebounceTimer = setTimeout(() => {
                    if (this.pendingTpPrice !== null && this.onModifyCallback && this.currentPosition) {
                      console.log(`[PositionOverlay] TP drag complete: → $${this.pendingTpPrice.toFixed(2)}`);
                      this.onModifyCallback("tp", this.pendingTpPrice, this.currentPosition);
                    }
                    this.tpDebounceTimer = null;
                    this.pendingTpPrice = null;
                  }, DEBOUNCE_DELAY_MS);
                }
              }
            }
          }
        }
      } catch (e) {
        // Shape might not exist
      }
    }

    // Check SL line
    if (this.slLineId && !this.isModifyingSl) {
      try {
        const shape = this.chart.getShapeById(this.slLineId);
        if (shape) {
          const points = shape.getPoints();
          if (points && points.length > 0) {
            const currentPrice = points[0].price;
            const lastKnown = this.lastKnownSlPrice;
            
            if (lastKnown !== null) {
              const priceChangePercent = Math.abs((currentPrice - lastKnown) / lastKnown);
              if (priceChangePercent > 0.00001) {
                // Price changed! Update tracking and schedule debounced callback
                this.lastKnownSlPrice = currentPrice;
                
                // Validate the new price
                if (validateSLPrice(currentPrice, this.currentPosition)) {
                  // Schedule debounced callback - reset timer if already pending
                  if (this.slDebounceTimer) {
                    clearTimeout(this.slDebounceTimer);
                  }
                  this.pendingSlPrice = currentPrice;
                  
                  this.slDebounceTimer = setTimeout(() => {
                    if (this.pendingSlPrice !== null && this.onModifyCallback && this.currentPosition) {
                      console.log(`[PositionOverlay] SL drag complete: → $${this.pendingSlPrice.toFixed(2)}`);
                      this.onModifyCallback("sl", this.pendingSlPrice, this.currentPosition);
                    }
                    this.slDebounceTimer = null;
                    this.pendingSlPrice = null;
                  }, DEBOUNCE_DELAY_MS);
                }
              }
            }
          }
        }
      } catch (e) {
        // Shape might not exist
      }
    }
  }

  /**
   * Update position lines on the chart using createShape API
   * @param position - The position to display
   * @param chart - TradingView chart API
   * @param _callbacks - Callbacks for TP/SL changes
   * @param partialTargets - Optional array of partial TP/SL targets
   */
  async updatePosition(
    position: Position,
    chart: TradingView.IChartWidgetApi,
    _callbacks?: TPSLChangeCallback,
    partialTargets?: PartialTarget[]
  ): Promise<void> {
    // Clear old lines
    this.clear();

    // Store current position and chart reference
    this.currentPosition = position;
    this.chart = chart;

    const isLong = position.side === "long";
    const entryPrice = position.entryPrice;
    const liquidationPrice = position.liquidationPrice;

    try {
      // Get current time from chart (rightmost visible bar)
      const currentTime = Date.now() / 1000; // Convert to seconds for TradingView

      // Create entry line (blue, solid, 1px, 90% opacity) - NOT draggable, NOT selectable
      // Using blue-500 (#3b82f6) for premium look - fully differentiated from TP (green) and SL (red)
      this.entryLineId = await chart.createShape(
        { time: currentTime, price: entryPrice },
        {
          shape: "horizontal_line",
          lock: true,
          disableSelection: true, // Prevent selection to avoid "Value is null" error
          disableSave: true,
          disableUndo: true,
          overrides: {
            linecolor: "rgba(59, 130, 246, 0.9)",
            linewidth: 1, // 1 pixel
            linestyle: 0, // Solid
            showPrice: true,
            textcolor: "rgba(59, 130, 246, 0.9)",
            horzLabelsAlign: "left",
            fontsize: 12,
            showLabel: true, // Show label
          },
          text: "Entry", // Just the label, no price
        }
      );

      // Create liquidation line (orange, solid, 1px, 80% opacity) - NOT draggable, NOT selectable
      this.liquidationLineId = await chart.createShape(
        { time: currentTime, price: liquidationPrice },
        {
          shape: "horizontal_line",
          lock: true,
          disableSelection: true, // Prevent selection to avoid "Value is null" error
          disableSave: true,
          disableUndo: true,
          overrides: {
            linecolor: "rgba(245, 158, 11, 0.8)",
            linewidth: 1, // 1 pixel
            linestyle: 0, // Solid (same as entry - non-movable lines are solid)
            showPrice: true,
            textcolor: "rgba(245, 158, 11, 0.8)",
            horzLabelsAlign: "left",
            fontsize: 12,
            showLabel: true, // Show label
          },
          text: "Liq", // Just the label, no price
        }
      );

      // If we have partial targets, draw those instead of standard TP/SL
      if (partialTargets && partialTargets.length > 0) {
        await this.drawPartialTargets(chart, position, partialTargets, currentTime);
      } else {
        // Standard TP/SL mode - draw single TP and SL lines
        
        // Create TP line if set (green, solid, 1px, 80% opacity) - DRAGGABLE!
        if (position.takeProfit && position.takeProfit > 0) {
          const tpPnl = (position.takeProfit - entryPrice) * position.size * (isLong ? 1 : -1);

          this.tpLineId = await chart.createShape(
            { time: currentTime, price: position.takeProfit },
            {
              shape: "horizontal_line",
              lock: false, // ← DRAGGABLE! User can move the line
              disableSelection: false,
              disableSave: true,
              disableUndo: true,
              overrides: {
                linecolor: "rgba(16, 185, 129, 0.8)",
                linewidth: 1, // 1 pixel
                linestyle: 0, // Solid
                showPrice: true,
                textcolor: "rgba(16, 185, 129, 0.8)",
                horzLabelsAlign: "left",
                fontsize: 12,
                showLabel: true,
              },
              text: `TP: +$${tpPnl.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
            }
          );
          
          this.lastKnownTpPrice = position.takeProfit;
        }

        // Create SL line if set (red, solid, 1px, 80% opacity) - DRAGGABLE!
        if (position.stopLoss && position.stopLoss > 0) {
          const slPnl = (position.stopLoss - entryPrice) * position.size * (isLong ? 1 : -1);

          this.slLineId = await chart.createShape(
            { time: currentTime, price: position.stopLoss },
            {
              shape: "horizontal_line",
              lock: false, // ← DRAGGABLE! User can move the line
              disableSelection: false,
              disableSave: true,
              disableUndo: true,
              overrides: {
                linecolor: "rgba(246, 70, 93, 0.8)",
                linewidth: 1, // 1 pixel
                linestyle: 0, // Solid
                showPrice: true,
                textcolor: "rgba(246, 70, 93, 0.8)",
                horzLabelsAlign: "left",
                fontsize: 12,
                showLabel: true,
              },
              text: `SL: -$${Math.abs(slPnl).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
            }
          );
          
          this.lastKnownSlPrice = position.stopLoss;
        }
        
        // Start price checking if we have TP or SL lines and a callback
        if ((this.tpLineId || this.slLineId) && this.onModifyCallback) {
          this.startPriceCheck();
        }
      }
    } catch (error) {
      console.error("[PositionOverlay] ❌ Error creating shapes:", error);
      throw error;
    }
  }
  
  /**
   * Draw multiple partial TP/SL target lines on the chart
   * Partial targets are NOT draggable (too complex to modify in-place)
   */
  private async drawPartialTargets(
    chart: TradingView.IChartWidgetApi,
    position: Position,
    targets: PartialTarget[],
    currentTime: number
  ): Promise<void> {
    const isLong = position.side === "long";
    const entryPrice = position.entryPrice;
    
    // Separate targets by type
    const tpTargets = targets.filter(t => t.targetType === "take_profit");
    const slTargets = targets.filter(t => t.targetType === "stop_loss");
    
    // Draw Take Profit lines (green, dotted)
    for (let i = 0; i < tpTargets.length; i++) {
      const target = tpTargets[i];
      const closeSize = target.closeAmountType === "percentage" 
        ? (target.closeAmountValue / 100) * position.size 
        : target.closeAmount;
      const tpPnl = (target.triggerPrice - entryPrice) * closeSize * (isLong ? 1 : -1);
      const percentLabel = target.closeAmountType === "percentage" 
        ? `${target.closeAmountValue}%` 
        : `${target.closeAmount.toFixed(4)}`;
      
      const lineId = await chart.createShape(
        { time: currentTime, price: target.triggerPrice },
        {
          shape: "horizontal_line",
          lock: true, // Partial targets are NOT draggable
          disableSelection: true, // Prevent selection - edit via position panel instead
          disableSave: true,
          disableUndo: true,
          overrides: {
            linecolor: "rgba(16, 185, 129, 0.8)",
            linewidth: 1,
            linestyle: 0, // Solid
            showPrice: true,
            textcolor: "rgba(16, 185, 129, 0.8)",
            horzLabelsAlign: "left",
            fontsize: 11,
            showLabel: true,
          },
          text: `TP${i + 1} (${percentLabel}): +$${tpPnl.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        }
      );
      
      if (lineId) {
        this.partialTpLineIds.push(lineId);
      }
    }
    
    // Draw Stop Loss lines (red, solid)
    for (let i = 0; i < slTargets.length; i++) {
      const target = slTargets[i];
      const closeSize = target.closeAmountType === "percentage" 
        ? (target.closeAmountValue / 100) * position.size 
        : target.closeAmount;
      const slPnl = (target.triggerPrice - entryPrice) * closeSize * (isLong ? 1 : -1);
      const percentLabel = target.closeAmountType === "percentage" 
        ? `${target.closeAmountValue}%` 
        : `${target.closeAmount.toFixed(4)}`;
      
      const lineId = await chart.createShape(
        { time: currentTime, price: target.triggerPrice },
        {
          shape: "horizontal_line",
          lock: true, // Partial targets are NOT draggable
          disableSelection: true, // Prevent selection - edit via position panel instead
          disableSave: true,
          disableUndo: true,
          overrides: {
            linecolor: "rgba(246, 70, 93, 0.8)",
            linewidth: 1,
            linestyle: 0, // Solid
            showPrice: true,
            textcolor: "rgba(246, 70, 93, 0.8)",
            horzLabelsAlign: "left",
            fontsize: 11,
            showLabel: true,
          },
          text: `SL${i + 1} (${percentLabel}): -$${Math.abs(slPnl).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
        }
      );
      
      if (lineId) {
        this.partialSlLineIds.push(lineId);
      }
    }
  }

  /**
   * Clear all position lines from the chart
   */
  clear(): void {
    this.stopPriceCheck();
    
    if (this.chart) {
      if (this.entryLineId) {
        try { this.chart.removeEntity(this.entryLineId); } catch (e) {}
      }
      if (this.liquidationLineId) {
        try { this.chart.removeEntity(this.liquidationLineId); } catch (e) {}
      }
      if (this.tpLineId) {
        try { this.chart.removeEntity(this.tpLineId); } catch (e) {}
      }
      if (this.slLineId) {
        try { this.chart.removeEntity(this.slLineId); } catch (e) {}
      }
      
      // Clear partial target lines
      for (const lineId of this.partialTpLineIds) {
        try { this.chart.removeEntity(lineId); } catch (e) {}
      }
      for (const lineId of this.partialSlLineIds) {
        try { this.chart.removeEntity(lineId); } catch (e) {}
      }
    }

    this.entryLineId = null;
    this.liquidationLineId = null;
    this.tpLineId = null;
    this.slLineId = null;
    this.partialTpLineIds = [];
    this.partialSlLineIds = [];
    this.currentPosition = null;
    this.lastKnownTpPrice = null;
    this.lastKnownSlPrice = null;
    this.pendingTpPrice = null;
    this.pendingSlPrice = null;
  }

  /**
   * Get the current position
   */
  getCurrentPosition(): Position | null {
    return this.currentPosition;
  }

  /**
   * Get TP line ID
   */
  getTpLineId(): TradingView.EntityId | null {
    return this.tpLineId;
  }

  /**
   * Get SL line ID
   */
  getSlLineId(): TradingView.EntityId | null {
    return this.slLineId;
  }
  
  /**
   * Set modifying flags to prevent duplicate callbacks
   */
  setModifying(type: "tp" | "sl", value: boolean): void {
    if (type === "tp") {
      this.isModifyingTp = value;
    } else {
      this.isModifyingSl = value;
    }
  }
}
